<template>
  <section 
    v-if="variants.v1 === variant"
    class="layout-pt-lg layout-pb-lg" 
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div class="container">
      <div v-if="vars.titleHtml" class="tw-mb-8 tw-w-full tw-text-center lg:tw-mb-12">
        <div v-html="vars.titleHtml"></div>
      </div>

      <!-- Loading State -->
      <div v-if="isManualBlogsLoading" class="tw-flex tw-items-center tw-justify-center tw-py-20">
        <loader />
      </div>

      <!-- No Blogs State -->
      <div v-else-if="!isManualBlogsLoading && manualBlogs.length === 0" class="tw-py-20 tw-text-center">
        <p class="tw-text-gray-500">No blogs found. Please check your configuration.</p>
      </div>

      <!-- Blogs Slider -->
      <div v-else class="relative">
        <div
          class="absoluteNavSlider overflow-hidden js-section-slider"
          data-gap="30"
          data-slider-cols="xl-2 lg-2 md-2 sm-2 base-1"
          data-nav-prev="js-manualBlogsSlider-prev"
          data-nav-next="js-manualBlogsSlider-next"
        >
          <div class="swiper-wrapper">
            <div 
              v-for="(blog, index) in manualBlogs" 
              :key="`manual-blog-${index}-${blog.url}`"
              class="swiper-slide !tw-h-full"
            >
              <nuxt-link :to="`/blogs/${blog.url}`" :external="true" class="n-link">
                <library-image 
                  v-if="blog.largeImage" 
                  :src="blog.largeImage" 
                  :h-ratio="9" 
                  :w-ratio="16"
                  :alt="blog.title"
                  :title="blog.title"
                />
                <div v-if="blog.title" class="tw-mt-4 tw-flex tw-flex-col tw-gap-3">
                  <h5>{{ blog.title }}</h5>
                  <p v-if="blog.description" class="tw-line-clamp-3">{{ blog.description }}</p>
                </div>
              </nuxt-link>
            </div>
          </div>
        </div>

        <div v-if="manualBlogs.length > 1" class="absoluteNav">
          <button class="sliderNav -prev js-manualBlogsSlider-prev">
            <i class="icon-left-arrow"></i>
          </button>
          <button class="sliderNav -next ml-20 js-manualBlogsSlider-next">
            <i class="icon-right-arrow"></i>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import type { Component } from '~/types';
import indexGlobal from '~/mixins/index.global';
import { ComponentMixin } from '~/mixins/component.mixin';
import { ManualBlogsMixin } from '~/units/blogs/mixins/manualBlogs.mixin';
import { ROUTES } from '~/constants/ui.constants';
import Loader from '~/components/common/Loader.vue';
import { fetchManualBlogsManager } from '~/composables/blogs';

export default defineNuxtComponent({
  name: 'Theme7ManualBlogsBox',

  mixins: [indexGlobal, ComponentMixin, ManualBlogsMixin],

  components: {
    Loader,
  },

  async setup(props: { component: Component }) {
    await callOnce(async () => {
      const nuxtApp = useNuxtApp();

      await nuxtApp.runWithContext(async () => {
        const variables = props.component.variables;
        // Extract slugs in numeric order of their index (blogs_1, blogs_2, ...)
        const blogSlugs: string[] = Object.keys(variables)
          .map((key) => {
            const match = key.match(/blogs_(\d+)_slug-text/);
            if (!match) return null;
            const slug = variables[key];
            if (!slug || typeof slug !== 'string' || !slug.trim()) return null;
            return { index: Number(match[1]), slug: slug.trim() };
          })
          .filter((x): x is { index: number; slug: string } => !!x)
          .sort((a, b) => a.index - b.index)
          .map((x) => x.slug);

        if (blogSlugs.length > 0) {
          await fetchManualBlogsManager(props.component, blogSlugs);
        }
      });
    });
  },

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
      },
    };
  },

  computed: {
    ROUTES() {
      return ROUTES;
    },

    cardVersion() {
      const version = this.vars.cardVersionText;
      return version ? String(version) : 'v2';
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (typeof window !== 'undefined' && (window as any).sectionSlider) {
        setTimeout(() => {
          (window as any).sectionSlider();
        }, 100);
      }
    });
  },
});
</script>

<style scoped>
.absoluteNav {
  pointer-events: none;
}

.absoluteNav .sliderNav {
  pointer-events: auto;
  z-index: 15 !important;
  transition: opacity 0.3s ease;
}

.absoluteNav .sliderNav.swiper-button-disabled {
  opacity: 0.3;
}

.absoluteNav .sliderNav:disabled {
  opacity: 0.3;
}
</style>
